import { memo, useMemo } from 'react'
import classNames from 'classnames'

import { TableDataViewCell, TableDataViewCellProgress } from '@aytm/app/stats/components/data-views/table/types'
import { NO_DATA, hasCellData } from '@aytm/app/stats/components/data-views/table/lib/cell-data'

// styles
import style from '@aytm/app/stats/components/data-views/table/styles/Table.module.scss'

type TableCellProgressProps = {
  cell: TableDataViewCell
  width: number | string
  percentDecimals?: number
  percentSign?: boolean
  format?: (value: number | string) => number | string
}

type StylesProps = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
  width?: string
}

export default memo(TableCellProgress)
export function TableCellProgress(props: TableCellProgressProps) {
  const cell = props.cell as TableDataViewCellProgress

  const styles = useMemo(() => {
    const values: StylesProps = {}
    if ('width' in props && Number.isFinite(props.width)) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = `${props.width}px`
      values.width = `${props.width}px`
    }
    if ('width' in props && 'string' == typeof props.width) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = props.width
      values.width = props.width
    }
    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.width])

  const cellAmount = useMemo(() => {
    if ('loading' in cell && cell.loading) return null
    if (!hasCellData(cell.amount)) return NO_DATA

    return 'format' in props && 'function' == typeof props.format ? props.format(cell.amount) : cell.amount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.format, cell.amount, cell.loading])

  const percentsLabel = useMemo(() => {
    if (!hasCellData(cell.percents)) return NO_DATA

    // the row always hands this down -- the column's precision, or null where the column carries
    // none -- so a key-presence test can never fall back, and the absent case formats to a whole
    // number instead of the one decimal the page defaults to. An explicit 0 still wins.
    const percents = cell.percents.toFixed(props.percentDecimals ?? 1)
    const sign = false === props.percentSign ? '' : '%' // opt-out for magnitude columns
    return `${percents}${sign}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell.percents, props.percentDecimals, props.percentSign])

  return (
    <div className={classNames(style['row-cell'], style['row-cell-progress'])} style={styles}>
      {'loading' in cell && cell.loading ? null : (
        <>
          {Number.isFinite(cell.share) ? (
            <span className={style['row-cell-progress-rail']}>
              {/* a row that has no share of the answers' scale keeps the rail and claims none of it */}
              {0 < cell.share ? (
                <i className={style['row-cell-progress-mark']} style={{ width: `${(cell.share * 100).toFixed(2)}%` }} />
              ) : null}
            </span>
          ) : null}
          {'amount' in cell ? <span className={style['row-cell-progress-amount']}>{cellAmount}</span> : null}
          {/* a cell with no share key has no share to state (a count-only list, a screener answer
              that ended the interview) and draws no slot; a share key with no number still dashes */}
          {'percents' in cell ? <span className={style['row-cell-progress-percents']}>{percentsLabel}</span> : null}
        </>
      )}
    </div>
  )
}
